import clsx from 'clsx'
import type { ReactNode } from 'react'
import { EditIcon, InfoIcon } from '../icons/icons'
import { IconButton } from './IconButton'
import { Tooltip } from './Tooltip'

export type InfoHintSize = 'sm' | 'md'

const ICON_SIZE: Record<InfoHintSize, number> = { sm: 12, md: 14 }

/**
 * An info icon that explains something in place, such as a record's
 * description. A click (or Enter) pins the card and goes no further, so it can
 * sit inside a clickable row. Renders nothing for empty text.
 */
export default function InfoHint({
  content,
  label,
  heading,
  size = 'md',
  align = 'left',
  className,
  onEdit,
  editLabel = 'Edit',
}: {
  content: ReactNode
  /** The icon's accessible name. */
  label: string
  heading?: string
  size?: InfoHintSize
  align?: 'left' | 'right'
  className?: string
  /** Gives the card a pencil at its top right, for someone who may change the text. */
  onEdit?: () => void
  /** The pencil's accessible name and tooltip. */
  editLabel?: string
}) {
  if (content === null || content === undefined || content === false) return null
  if (typeof content === 'string' && !content.trim()) return null
  return (
    <Tooltip
      clickToggle
      align={align}
      className={clsx('shrink-0', className)}
      contentClassName="!w-max max-w-80 rounded-lg px-3 py-2.5 shadow-lg"
      content={
        <div className="flex flex-col gap-1">
          {heading || onEdit ? (
            <div className="flex min-h-3 items-center justify-between gap-3">
              {heading ? (
                <span className="text-[10px] font-semibold uppercase leading-3 tracking-[0.08em] text-muted">
                  {heading}
                </span>
              ) : (
                <span />
              )}
              {onEdit ? (
                <IconButton
                  label={editLabel}
                  onClick={(event) => {
                    event.stopPropagation()
                    onEdit()
                  }}
                  wrapperClassName="-my-1 -mr-1.5 flex"
                  className="flex h-6 w-6 items-center justify-center rounded text-icon-quiet transition-colors hover:bg-lighter hover:text-default"
                >
                  <EditIcon size={12} aria-hidden="true" />
                </IconButton>
              ) : null}
            </div>
          ) : null}
          <div className="whitespace-pre-line break-words text-sm font-normal leading-5 text-default">
            {typeof content === 'string' ? content.trim() : content}
          </div>
        </div>
      }
    >
      <span
        tabIndex={0}
        role="button"
        aria-label={label}
        className={clsx(
          'inline-flex shrink-0 cursor-pointer items-center justify-center rounded text-icon-quiet outline-none transition-colors hover:text-subtle focus-visible:ring-2 focus-visible:ring-[color:var(--accent-info)]',
          size === 'md' ? 'h-5 w-5' : 'h-4 w-4'
        )}
      >
        <InfoIcon size={ICON_SIZE[size]} aria-hidden="true" />
      </span>
    </Tooltip>
  )
}
